/**
 * PolygonDrawer Component
 *
 * Visual overlay for drawing new polygon regions in the map editor.
 * Shows the points being drawn and preview lines connecting them.
 *
 * Features:
 * - Renders current polygon points as circles
 * - Shows lines connecting the points
 * - Preview line from last point to cursor position
 * - Different styling for incomplete vs complete polygon
 *
 * @example
 * ```tsx
 * <PolygonDrawer
 *   points={currentPolygon}
 *   imageWidth={1920}
 *   imageHeight={1080}
 *   isComplete={currentPolygon.length >= 3}
 * />
 * ```
 */

import { useMemo } from 'react';
import { Line, Circle, Group } from 'react-konva';
import type { Point } from '@flash/shared';
import { denormalizePoint, type Dimensions } from '@/utils/coordinates';

/**
 * Props for PolygonDrawer component
 */
export interface PolygonDrawerProps {
  /** Points of the polygon being drawn (normalized 0-1 coordinates) */
  points: Point[];
  /** Image/canvas width for denormalization */
  imageWidth: number;
  /** Image/canvas height for denormalization */
  imageHeight: number;
  /** Whether the polygon has enough points to be closed (>= 3) */
  isComplete?: boolean;
  /** Cursor position for preview line (normalized coordinates, optional) */
  cursorPosition?: Point | null;
  /** Whether currently snapping to an existing region */
  isSnapping?: boolean;
  /** Line color (default: blue) */
  lineColor?: string;
  /** Point color (default: blue) */
  pointColor?: string;
  /** Point radius in pixels (default: 6) */
  pointRadius?: number;
}

/** Default colors */
const DEFAULTS = {
  lineColor: '#3b82f6',
  lineColorComplete: '#10b981',
  pointColor: '#3b82f6',
  pointColorComplete: '#10b981',
  previewLineColor: 'rgba(59, 130, 246, 0.5)',
  snapColor: '#f59e0b', // Amber for snapping indicator
  pointRadius: 6,
  lineWidth: 2,
  previewLineWidth: 1,
};

/**
 * PolygonDrawer Component
 *
 * Renders the polygon currently being drawn with visual feedback.
 */
export const PolygonDrawer = ({
  points,
  imageWidth,
  imageHeight,
  isComplete = false,
  cursorPosition,
  isSnapping = false,
  lineColor,
  pointColor,
  pointRadius = DEFAULTS.pointRadius,
}: PolygonDrawerProps) => {
  const dimensions: Dimensions = useMemo(
    () => ({ width: imageWidth, height: imageHeight }),
    [imageWidth, imageHeight]
  );

  // Convert normalized points to pixel coordinates
  const pixelPoints = useMemo(() => {
    return points.map((point) => denormalizePoint(point, dimensions));
  }, [points, dimensions]);

  // Flatten points for Konva Line component
  const flatPoints = useMemo(() => {
    return pixelPoints.flatMap((p) => [p.x, p.y]);
  }, [pixelPoints]);

  // Calculate preview line from last point to cursor
  const previewLine = useMemo(() => {
    if (!cursorPosition || pixelPoints.length === 0) return null;

    const lastPoint = pixelPoints[pixelPoints.length - 1]!;
    const cursorPixel = denormalizePoint(cursorPosition, dimensions);

    return [lastPoint.x, lastPoint.y, cursorPixel.x, cursorPixel.y];
  }, [cursorPosition, pixelPoints, dimensions]);

  // Calculate closing line preview (from cursor to first point)
  const closingLine = useMemo(() => {
    if (!cursorPosition || pixelPoints.length < 2) return null;

    const firstPoint = pixelPoints[0]!;
    const cursorPixel = denormalizePoint(cursorPosition, dimensions);

    return [cursorPixel.x, cursorPixel.y, firstPoint.x, firstPoint.y];
  }, [cursorPosition, pixelPoints, dimensions]);

  // Determine colors based on completion state
  const currentLineColor =
    lineColor ?? (isComplete ? DEFAULTS.lineColorComplete : DEFAULTS.lineColor);
  const currentPointColor =
    pointColor ?? (isComplete ? DEFAULTS.pointColorComplete : DEFAULTS.pointColor);

  // Don't render if no points
  if (points.length === 0) {
    return null;
  }

  return (
    <Group>
      {/* Main polygon line (not closed yet) */}
      {flatPoints.length >= 4 && (
        <Line
          points={flatPoints}
          stroke={currentLineColor}
          strokeWidth={DEFAULTS.lineWidth}
          lineCap="round"
          lineJoin="round"
        />
      )}

      {/* Preview line from last point to cursor */}
      {previewLine && (
        <Line
          points={previewLine}
          stroke={DEFAULTS.previewLineColor}
          strokeWidth={DEFAULTS.previewLineWidth}
          dash={[5, 5]}
        />
      )}

      {/* Closing line preview (from cursor to first point) when nearly complete */}
      {closingLine && isComplete && (
        <Line
          points={closingLine}
          stroke={DEFAULTS.previewLineColor}
          strokeWidth={DEFAULTS.previewLineWidth}
          dash={[5, 5]}
        />
      )}

      {/* Point markers */}
      {pixelPoints.map((point, index) => (
        <Circle
          key={index}
          x={point.x}
          y={point.y}
          radius={pointRadius}
          fill={index === 0 && isComplete ? '#10b981' : currentPointColor}
          stroke="#ffffff"
          strokeWidth={2}
          // First point is special when polygon can be closed

          scaleX={index === 0 && isComplete ? 1.3 : 1}
          scaleY={index === 0 && isComplete ? 1.3 : 1}
        />
      ))}

      {/* Snap indicator at cursor position */}
      {cursorPosition && isSnapping && (
        <Circle
          x={denormalizePoint(cursorPosition, dimensions).x}
          y={denormalizePoint(cursorPosition, dimensions).y}
          radius={pointRadius + 4}
          fill="transparent"
          stroke={DEFAULTS.snapColor}
          strokeWidth={3}
        />
      )}
    </Group>
  );
};

export default PolygonDrawer;
